import { ref } from "vue";
import type { Ref } from "vue";

export type AutorizacaoAvulsaParams = {
	ID_Empresa: string;
	ID_Modulo: string;
	ID_Liberacao: string;
	LiberaUsuLogado?: boolean;
	Tabela?: string;
	ChaveTabela?: string;
	UsuLib?: number;
	Motivo?: string;
	/**
	 * Exige o motivo da liberação, como o `SolicitaMotivo` do
	 * `SenhaParaAutorizacao_Local` do Delphi.
	 *
	 * No original há duas portas para a mesma janela: `SenhaParaAutorizacao`,
	 * que pede só usuário e senha, e `SenhaParaAutorizacao_UsuLibMotivo`, que
	 * acende o `PanelMotivo` e recusa confirmar com o campo vazio
	 * (`FUNC_SENHA_AUTORIZA.pas:320-322`). Aqui a escolha é desta bandeira.
	 */
	SolicitaMotivo?: boolean;
};

export type UsuariosComPermissaoItem = {
	USU_FK_852: number;
	DESCR_754: string;
	/** Descrição da liberação; igual em todas as linhas. */
	DESC_LIBERACAO_852?: string | null;
};

export type UsuariosComPermissaoParams = {
	EMPRESA: number;
	MODULO: string;
	LIBERACAO: string;
	USUARIO: number;
};

export type ValidarAutorizacaoParams = {
	EMPRESA: number;
	MODULO: string;
	LIBERACAO: string;
	USUARIO_LIBERACAO: string;
	USUARIO_BLOQUEADO: number;
	SENHA: string;
};

export type UseAutorizacaoAvulsaReturn = {
	visible: Ref<boolean>;
	idEmpresa: Ref<string>;
	idModulo: Ref<string>;
	idLiberacao: Ref<string>;
	liberaUsuarioLogado: Ref<boolean>;
	tabela: Ref<string>;
	chaveTabela: Ref<string>;
	usuLib: Ref<number>;
	motivo: Ref<string | null>;
	solicitaMotivo: Ref<boolean>;
	usuarioLiberacao: Ref<string>;
	senhaLiberacao: Ref<string>;
	modulo: Ref<string>;
	liberacao: Ref<string>;
	dialogUsuariosComPermissaoVisible: Ref<boolean>;
	usuariosComPermissao: Ref<UsuariosComPermissaoItem[]>;
	obterAutorizacao: (params: Partial<AutorizacaoAvulsaParams>) => Promise<boolean>;
	show: (pModulo: string, pLiberacao: number | string) => void;
	senhaParaAutorizacao: (params?: Partial<AutorizacaoAvulsaParams>) => Promise<boolean>;
	handleResponse: (value: boolean) => void;
	close: () => void;
	closeUsuariosComPermissaoDialog: () => void;
	buildValidarAutorizacaoParams: (
		companyID: number,
		userID: number,
		idModulo: string,
		idLiberacao: string,
	) => ValidarAutorizacaoParams;
	buildListarUsuariosParams: (companyID: number, userID: number) => UsuariosComPermissaoParams;
	descricaoLiberacao: Ref<string>;
	setUsuariosComPermissao: (lista: UsuariosComPermissaoItem[], abrirJanela?: boolean) => void;
	clearAfterConfirm: () => void;
	onMessageDialogClosed: (focusCallback: () => void) => void;
};

const visible = ref(false);
const idEmpresa = ref("");
const idModulo = ref("");
const idLiberacao = ref("");
const liberaUsuarioLogado = ref(false);
const tabela = ref("");
const chaveTabela = ref("");
const usuLib = ref(0);
const motivo = ref<string | null>(null);
const solicitaMotivo = ref(false);
const usuarioLiberacao = ref("");
const senhaLiberacao = ref("");
const modulo = ref("");
const liberacao = ref("");
const dialogUsuariosComPermissaoVisible = ref(false);
const usuariosComPermissao = ref<UsuariosComPermissaoItem[]>([]);
/**
 * Texto que explica a liberação pedida, vindo de `DESC_LIBERACAO_852`.
 *
 * Fica vazio quando a liberação não está cadastrada para a empresa — caso em que
 * a tela avisa, em vez de mostrar só o código e deixar o operador adivinhando.
 */
const descricaoLiberacao = ref("");

let localResolve: ((value: boolean | PromiseLike<boolean>) => void) | null = null;

/**
 * Descrição da liberação, pela que mais se repete entre os autorizadores.
 *
 * `DESC_LIBERACAO_852` é gravada em CADA linha de usuário, não numa tabela de
 * liberações — então a mesma liberação pode ter textos diferentes conforme quem
 * cadastrou. Na base da dev a W33 tem duas: 5 linhas com "Liberação de Corte na
 * Conferência Consolidada" e 3 com o texto da W24, herdado por cópia. Pegar
 * `lista[0]` fazia a tela anunciar a liberação errada.
 *
 * A correção definitiva é no cadastro da rotina 1152; aqui a tela apenas deixa
 * de depender da ordem em que o banco devolveu as linhas.
 */
function descricaoMaisFrequente(lista: UsuariosComPermissaoItem[]): string {
	const contagem = new Map<string, number>();

	for (const item of lista) {
		const descricao = (item.DESC_LIBERACAO_852 ?? "").trim();
		if (descricao)
			contagem.set(descricao, (contagem.get(descricao) ?? 0) + 1);
	}

	let escolhida = "";
	let maior = 0;

	for (const [descricao, vezes] of contagem) {
		if (vezes > maior) {
			escolhida = descricao;
			maior = vezes;
		}
	}

	return escolhida;
}

function syncModuloLiberacao(pModulo?: string, pLiberacao?: string): void {
	if (pModulo !== undefined) {
		modulo.value = pModulo;
		idModulo.value = pModulo;
	}

	if (pLiberacao !== undefined) {
		liberacao.value = pLiberacao;
		idLiberacao.value = pLiberacao;
	}
}

function applyCommonAuthorizationParams(params: Partial<AutorizacaoAvulsaParams>): void {
	if (params.ID_Empresa !== undefined)
		idEmpresa.value = params.ID_Empresa;

	if (params.ID_Modulo !== undefined)
		syncModuloLiberacao(params.ID_Modulo, params.ID_Liberacao);
	else if (params.ID_Liberacao !== undefined)
		syncModuloLiberacao(undefined, params.ID_Liberacao);

	if (params.LiberaUsuLogado !== undefined)
		liberaUsuarioLogado.value = params.LiberaUsuLogado;

	if (params.Tabela !== undefined)
		tabela.value = params.Tabela;

	if (params.ChaveTabela !== undefined)
		chaveTabela.value = params.ChaveTabela;

	if (params.UsuLib !== undefined)
		usuLib.value = params.UsuLib;

	/*
	 * Atribuição incondicional, e não `if (!== undefined)`: os refs deste
	 * composable são de módulo, compartilhados por todas as telas. Só atribuir
	 * quando o parâmetro vier definido deixaria a exigência do motivo — e o
	 * texto digitado na liberação anterior — vivos para a liberação seguinte,
	 * que pode não pedir motivo nenhum.
	 *
	 * O motivo é zerado AQUI, na abertura, e não em `clearFields`: quem chamou
	 * precisa lê-lo depois de `obterAutorizacao` resolver, e `clearFields` roda
	 * logo após a confirmação.
	 */
	motivo.value = params.Motivo ?? null;
	solicitaMotivo.value = params.SolicitaMotivo ?? false;
}

function clearFields(): void {
	usuarioLiberacao.value = "";
	senhaLiberacao.value = "";
}

export const useAutorizacaoAvulsa = (): UseAutorizacaoAvulsaReturn => {
	const obterAutorizacao = (params: Partial<AutorizacaoAvulsaParams>): Promise<boolean> => {
		applyCommonAuthorizationParams(params);
		visible.value = true;

		return new Promise<boolean>((resolve) => {
			localResolve = resolve;
		});
	};

	const show = (pModulo: string, pLiberacao: number | string): void => {
		syncModuloLiberacao(String(pModulo), String(pLiberacao));
		visible.value = true;
	};

	const senhaParaAutorizacao = (params: Partial<AutorizacaoAvulsaParams> = {}): Promise<boolean> => {
		applyCommonAuthorizationParams(params);
		visible.value = true;

		return new Promise<boolean>((resolve) => {
			localResolve = resolve;
		});
	};

	const handleResponse = (value: boolean): void => {
		if (localResolve) {
			localResolve(value);
			localResolve = null;
		}

		if (value)
			close();
	};

	const close = (): void => {
		visible.value = false;
		clearFields();
	};

	const closeUsuariosComPermissaoDialog = (): void => {
		dialogUsuariosComPermissaoVisible.value = false;
	};

	const buildValidarAutorizacaoParams = (
		companyID: number,
		userID: number,
		idModuloValue: string,
		idLiberacaoValue: string,
	): ValidarAutorizacaoParams => ({
		EMPRESA: companyID,
		MODULO: idModuloValue,
		LIBERACAO: idLiberacaoValue,
		USUARIO_LIBERACAO: usuarioLiberacao.value,
		USUARIO_BLOQUEADO: userID,
		SENHA: senhaLiberacao.value,
	});

	const buildListarUsuariosParams = (
		companyID: number,
		userID: number,
	): UsuariosComPermissaoParams => ({
		EMPRESA: companyID,
		MODULO: modulo.value || idModulo.value,
		LIBERACAO: liberacao.value || idLiberacao.value,
		USUARIO: userID,
	});

	/**
	 * Guarda a lista e abre a janela.
	 *
	 * @param abrirJanela `false` quando a busca serve só para descobrir a
	 * descrição da liberação ao abrir o diálogo — aí nada é exibido.
	 */
	const setUsuariosComPermissao = (
		lista: UsuariosComPermissaoItem[],
		abrirJanela = true,
	): void => {
		usuariosComPermissao.value = lista;
		descricaoLiberacao.value = descricaoMaisFrequente(lista);

		if (abrirJanela) {
			dialogUsuariosComPermissaoVisible.value = true;
		}
	};

	const clearAfterConfirm = (): void => {
		clearFields();
	};

	const onMessageDialogClosed = (focusCallback: () => void): void => {
		senhaLiberacao.value = "";
		focusCallback();
	};

	return {
		visible,
		idEmpresa,
		idModulo,
		idLiberacao,
		liberaUsuarioLogado,
		tabela,
		chaveTabela,
		usuLib,
		motivo,
		solicitaMotivo,
		usuarioLiberacao,
		senhaLiberacao,
		modulo,
		liberacao,
		dialogUsuariosComPermissaoVisible,
		usuariosComPermissao,
		descricaoLiberacao,
		obterAutorizacao,
		show,
		senhaParaAutorizacao,
		handleResponse,
		close,
		closeUsuariosComPermissaoDialog,
		buildValidarAutorizacaoParams,
		buildListarUsuariosParams,
		setUsuariosComPermissao,
		clearAfterConfirm,
		onMessageDialogClosed,
	};
};